/**
 * MarkdownRenderer — Safe, design-system-aligned markdown rendering for Qlivar.
 *
 * Uses react-markdown + remark-gfm + rehype-sanitize.
 * Two variants:
 *   - "chat"  — inside chat bubbles; text-inherit, compact spacing, no headings
 *   - "prose" — standalone surfaces (dashboard fields, previews); full typography
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import type { Components } from 'react-markdown';
import { cn } from '@/lib/utils';

// ── Sanitize schema ───────────────────────────────────────────────────────────
// Extend the default safe schema to allow target/rel on anchors for
// external links. Scripts, style injections and event handlers remain blocked.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      'target',
      'rel',
    ],
  },
};

// ── Props ─────────────────────────────────────────────────────────────────────
interface MarkdownRendererProps {
  /** The markdown string to render */
  children: string;
  /**
   * Rendering context:
   * - "chat"  → compact, text-inherit, no outer margins (default)
   * - "prose" → full Qlivar typography for dashboard/knowledge surfaces
   */
  variant?: 'chat' | 'prose';
  /** Extra class names applied to the wrapper div */
  className?: string;
}

// ── Chat component map ────────────────────────────────────────────────────────
// These components are used inside chat bubbles. Color inherits from the bubble.
function buildChatComponents(): Components {
  return {
    // Paragraphs — compact, correct line-height
    p({ children }) {
      return (
        <p className="text-sm leading-relaxed [&:not(:first-child)]:mt-2 [&:not(:last-child)]:mb-0">
          {children}
        </p>
      );
    },

    // Strong — slightly heavier, avoids visual heaviness in colored bubbles
    strong({ children }) {
      return <strong className="font-semibold">{children}</strong>;
    },

    // Emphasis
    em({ children }) {
      return <em className="italic">{children}</em>;
    },

    // Links — underline + accent color with safe attributes
    a({ href, children }) {
      const isExternal =
        href?.startsWith('http://') || href?.startsWith('https://');
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="underline underline-offset-2 opacity-90 hover:opacity-100 transition-opacity decoration-current"
        >
          {children}
        </a>
      );
    },

    // Unordered list
    ul({ children }) {
      return (
        <ul className="text-sm space-y-0.5 pl-4 list-disc [&:not(:first-child)]:mt-2 [&:not(:last-child)]:mb-0 marker:opacity-60">
          {children}
        </ul>
      );
    },

    // Ordered list
    ol({ children }) {
      return (
        <ol className="text-sm space-y-0.5 pl-4 list-decimal [&:not(:first-child)]:mt-2 [&:not(:last-child)]:mb-0 marker:opacity-60">
          {children}
        </ol>
      );
    },

    // List item
    li({ children }) {
      return <li className="leading-relaxed">{children}</li>;
    },

    // Inline code
    code({ children, className: langClass }) {
      const isBlock = langClass?.startsWith('language-');
      if (isBlock) {
        // Fenced code block inside chat — render inline-ish to avoid layout break
        return (
          <code className="block text-xs font-mono bg-black/10 dark:bg-white/10 rounded px-2 py-1 mt-1 whitespace-pre-wrap break-words">
            {children}
          </code>
        );
      }
      return (
        <code className="text-xs font-mono bg-black/10 dark:bg-white/10 rounded px-1 py-0.5">
          {children}
        </code>
      );
    },

    // Block quotes — subtle left border
    blockquote({ children }) {
      return (
        <blockquote className="pl-3 border-l-2 border-current/30 opacity-80 text-sm italic [&:not(:first-child)]:mt-2">
          {children}
        </blockquote>
      );
    },

    // Horizontal rule
    hr() {
      return <hr className="my-2 border-current/20" />;
    },

    // Suppress h1-h6 in chat context — downgrade to bold paragraph
    h1({ children }) {
      return <p className="text-sm font-semibold mt-2 mb-0.5">{children}</p>;
    },
    h2({ children }) {
      return <p className="text-sm font-semibold mt-2 mb-0.5">{children}</p>;
    },
    h3({ children }) {
      return <p className="text-sm font-medium mt-1.5 mb-0">{children}</p>;
    },
    h4({ children }) {
      return <p className="text-sm font-medium mt-1.5 mb-0">{children}</p>;
    },
    h5({ children }) {
      return <p className="text-sm font-medium mt-1">{children}</p>;
    },
    h6({ children }) {
      return <p className="text-sm mt-1">{children}</p>;
    },

    // Table — scrollable wrapper
    table({ children }) {
      return (
        <div className="overflow-x-auto mt-2 text-xs">
          <table className="w-full border-collapse">{children}</table>
        </div>
      );
    },
    thead({ children }) {
      return <thead className="border-b border-current/20">{children}</thead>;
    },
    th({ children }) {
      return <th className="px-2 py-1 text-left font-semibold">{children}</th>;
    },
    td({ children }) {
      return (
        <td className="px-2 py-1 border-b border-current/10">{children}</td>
      );
    },
  };
}

// ── Prose component map ───────────────────────────────────────────────────────
// Used in dashboard fields / knowledge surfaces. Full Qlivar typography.
function buildProseComponents(): Components {
  return {
    p({ children }) {
      return (
        <p className="text-sm text-foreground leading-relaxed [&:not(:first-child)]:mt-3">
          {children}
        </p>
      );
    },

    strong({ children }) {
      return (
        <strong className="font-semibold text-foreground">{children}</strong>
      );
    },

    em({ children }) {
      return <em className="italic text-foreground/80">{children}</em>;
    },

    a({ href, children }) {
      const isExternal =
        href?.startsWith('http://') || href?.startsWith('https://');
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors font-medium"
        >
          {children}
        </a>
      );
    },

    ul({ children }) {
      return (
        <ul className="text-sm text-foreground space-y-1 pl-5 list-disc [&:not(:first-child)]:mt-3 marker:text-muted-foreground">
          {children}
        </ul>
      );
    },

    ol({ children }) {
      return (
        <ol className="text-sm text-foreground space-y-1 pl-5 list-decimal [&:not(:first-child)]:mt-3 marker:text-muted-foreground">
          {children}
        </ol>
      );
    },

    li({ children }) {
      return <li className="leading-relaxed">{children}</li>;
    },

    code({ children, className: langClass }) {
      const isBlock = langClass?.startsWith('language-');
      if (isBlock) {
        return (
          <code className="block text-xs font-mono bg-muted text-foreground rounded-md px-3 py-2 mt-2 whitespace-pre-wrap break-words border border-border">
            {children}
          </code>
        );
      }
      return (
        <code className="text-xs font-mono bg-muted text-foreground rounded px-1.5 py-0.5 border border-border">
          {children}
        </code>
      );
    },

    blockquote({ children }) {
      return (
        <blockquote className="pl-4 border-l-2 border-primary/40 text-muted-foreground text-sm italic [&:not(:first-child)]:mt-3">
          {children}
        </blockquote>
      );
    },

    hr() {
      return <hr className="my-4 border-border" />;
    },

    h1({ children }) {
      return (
        <h1 className="text-lg font-semibold text-foreground tracking-tight mt-4 mb-1 first:mt-0">
          {children}
        </h1>
      );
    },
    h2({ children }) {
      return (
        <h2 className="text-base font-semibold text-foreground tracking-tight mt-4 mb-1 first:mt-0">
          {children}
        </h2>
      );
    },
    h3({ children }) {
      return (
        <h3 className="text-sm font-semibold text-foreground mt-3 mb-0.5 first:mt-0">
          {children}
        </h3>
      );
    },
    h4({ children }) {
      return (
        <h4 className="text-sm font-medium text-foreground mt-3 mb-0.5 first:mt-0">
          {children}
        </h4>
      );
    },
    h5({ children }) {
      return (
        <h5 className="text-sm font-medium text-muted-foreground mt-2 first:mt-0">
          {children}
        </h5>
      );
    },
    h6({ children }) {
      return (
        <h6 className="text-sm text-muted-foreground mt-2 first:mt-0">
          {children}
        </h6>
      );
    },

    table({ children }) {
      return (
        <div className="overflow-x-auto mt-3 rounded-lg border border-border">
          <table className="w-full text-sm border-collapse">{children}</table>
        </div>
      );
    },
    thead({ children }) {
      return (
        <thead className="bg-muted/50 border-b border-border">{children}</thead>
      );
    },
    th({ children }) {
      return (
        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {children}
        </th>
      );
    },
    td({ children }) {
      return (
        <td className="px-3 py-2 text-foreground border-b border-border/50 last:border-b-0">
          {children}
        </td>
      );
    },
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
export function MarkdownRenderer({
  children,
  variant = 'chat',
  className,
}: MarkdownRendererProps) {
  const components = variant === 'prose'
    ? buildProseComponents()
    : buildChatComponents();

  return (
    <div
      className={cn(
        // Base reset — prevent prose plugin interference
        'min-w-0 break-words',
        // Remove top margin on first child, bottom margin on last
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
        components={components}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
